import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '../../context/I18nContext';
import { useSlidingWindow } from '../../hooks/useSlidingWindow';
import { isDemoMode } from '../../services/dataScope';
import { Lead } from '../../types';
import Card, { CardHeader, CardTitle, CardContent } from '../ui/Card';
import Button from '../ui/Button';
import { AlertCircle, Plus } from 'lucide-react';
import SlidingWindowNav from './SlidingWindowNav';
import {
  DASHBOARD_WINDOW_LIST,
  DASHBOARD_WINDOW_ROW,
  DASHBOARD_WINDOW_SIZE,
} from './dashboardWindowLayout';

interface UrgentFollowupsCardProps {
  leads: Lead[];
  /** Empty workspace: invite creating the first lead instead of a passive empty state. */
  showFirstLeadCta?: boolean;
  onCreateFirstLead?: () => void;
}

const UrgentFollowupsCard: React.FC<UrgentFollowupsCardProps> = ({
  leads,
  showFirstLeadCta = false,
  onCreateFirstLead,
}) => {
  const { t } = useI18n();
  const navigate = useNavigate();
  const interactive = !isDemoMode();
  const { visible, canPrev, canNext, prev, next, showNav } = useSlidingWindow(
    leads,
    DASHBOARD_WINDOW_SIZE
  );

  if (leads.length === 0) {
    return (
      <Card className="h-full flex flex-col">
        <CardHeader>
          <div className="flex items-center justify-between gap-2 min-h-7">
            <CardTitle className="flex items-center text-base">
              <AlertCircle className={`w-5 h-5 mr-2 ${showFirstLeadCta ? 'text-red-500' : 'text-gray-500'}`} />
              {t('dashboard.urgentActions')}
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent className="flex-1 flex flex-col items-center justify-center gap-3 px-4 text-center">
          {showFirstLeadCta && onCreateFirstLead ? (
            <>
              <p className="text-sm text-gray-600 leading-snug">{t('dashboard.firstLeadHint')}</p>
              <Button
                type="button"
                variant="primary"
                size="sm"
                icon={<Plus className="w-4 h-4" />}
                onClick={onCreateFirstLead}
              >
                {t('dashboard.firstLeadCta')}
              </Button>
            </>
          ) : (
            <p className="text-gray-500">{t('dashboard.noFollowUpScheduled')}</p>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="h-full flex flex-col">
      <CardHeader>
        <div className="flex items-center justify-between gap-2 min-h-7">
          <CardTitle className="flex items-center text-base min-w-0">
            <AlertCircle className="w-5 h-5 text-red-500 mr-2 shrink-0" />
            <span className="truncate">{t('dashboard.urgentActions')}</span>
          </CardTitle>
          {showNav && (
            <SlidingWindowNav
              canPrev={canPrev}
              canNext={canNext}
              onPrev={prev}
              onNext={next}
              tone="red"
            />
          )}
        </div>
      </CardHeader>
      <CardContent className="flex-1">
        <div className={DASHBOARD_WINDOW_LIST}>
          {visible.map((task) => (
            <div
              key={task.id}
              className={`flex items-center justify-between px-4 ${DASHBOARD_WINDOW_ROW} bg-red-50/80 rounded-xl border border-red-200/60 transition-colors duration-150 ${
                interactive
                  ? 'hover:bg-red-50 hover:border-red-300 cursor-pointer'
                  : ''
              }`}
              onClick={
                interactive
                  ? () => navigate(`/app/leads?lead=${task.id}#followups`)
                  : undefined
              }
              role={interactive ? 'button' : undefined}
              tabIndex={interactive ? 0 : undefined}
              onKeyDown={
                interactive
                  ? (e) => {
                      if (e.key === 'Enter') {
                        navigate(`/app/leads?lead=${task.id}#followups`);
                      }
                    }
                  : undefined
              }
            >
              <div className="min-w-0">
                <p className="font-semibold text-gray-900 truncate">{task.name}</p>
                <p className="text-sm text-gray-600 mt-0.5 truncate">
                  {t('dashboard.followUpScheduled')}
                </p>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export default UrgentFollowupsCard;
